import {
  Mapping,
  ModelConceptRelationship,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isModelConceptRelationship,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import { addRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/addRelationshipMapping';
import { isValidMapping } from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';

/** used for mapping relationships that are created or reconnected onto two valid
 * mapped classes that are mapped to the same resource. Mappings are created for all
 * similar relationships that connect classes that are mapped by the common resource
 */
export const addMappingsToConnectedValidRelationships =
  (relationships: ModelConceptRelationship[]) => (model: Model) => {
    relationships.forEach((relData) => {
      if (!relData.from || !relData.to || relData.from === relData.to) {
        return;
      }
      const fromNodeMappings: Mapping[] = [];
      const toNodeMappings: Mapping[] = [];

      model.linkDataArray.forEach((linkData) => {
        if (!isMapping(linkData)) {
          return;
        }
        if (linkData.to === relData.from && isValidMapping(linkData)(model)) {
          fromNodeMappings.push(linkData);
        }
        // remember that classes can have relationships to themselves
        if (linkData.to === relData.to && isValidMapping(linkData)(model)) {
          toNodeMappings.push(linkData);
        }
      });

      if (!fromNodeMappings.length || !toNodeMappings.length) {
        return;
      }

      const validMappings = fromNodeMappings.reduce<
        Array<{ fromNodeMapping: Mapping; toNodeMapping: Mapping }>
      >((acc, fromNodeMapping) => {
        toNodeMappings.forEach((toNodeMapping) => {
          if (fromNodeMapping.from === toNodeMapping.from) {
            acc.push({ fromNodeMapping, toNodeMapping });
          }
        });
        return acc;
      }, []);

      const usedRelationshipKeys = new Set<string>();
      validMappings.forEach(({ fromNodeMapping, toNodeMapping }) => {
        const relationshipDataArray = model.linkDataArray.filter(
          (linkData): linkData is ModelConceptRelationship =>
            isModelConceptRelationship(linkData) &&
            linkData.from === fromNodeMapping.to &&
            linkData.to === toNodeMapping.to
        );

        relationshipDataArray.forEach((relationshipData) => {
          if (usedRelationshipKeys.has(relationshipData.key)) {
            return;
          }

          if (!toNodeMapping.to || !fromNodeMapping.to) {
            return;
          }

          const existingRelMapping = model.linkDataArray.find(
            (linkData): linkData is RelationshipMapping =>
              isRelationshipMapping(linkData) &&
              linkData.relationshipIri === relationshipData.value &&
              Boolean(
                (linkData.from === fromNodeMapping.key &&
                  toNodeMapping.to &&
                  linkData.toClassIri ===
                    getModelConceptClassValue(toNodeMapping.to)) ||
                  (linkData.to === toNodeMapping.key &&
                    fromNodeMapping.to &&
                    linkData.fromClassIri ===
                      getModelConceptClassValue(fromNodeMapping.to))
              )
          );
          if (existingRelMapping) {
            return;
          }

          usedRelationshipKeys.add(relationshipData.key);
          addRelationshipMapping({
            from: fromNodeMapping.key,
            relationshipIri: relationshipData.value,
            toClassIri: getModelConceptClassValue(toNodeMapping.to),
            toPrimaryIdentifier: {
              baseIri: toNodeMapping.primaryIdentifier.baseIri,
              parts: [...toNodeMapping.primaryIdentifier.parts],
            },
          })(model);
          addRelationshipMapping({
            fromClassIri: getModelConceptClassValue(fromNodeMapping.to),
            fromPrimaryIdentifier: {
              baseIri: fromNodeMapping.primaryIdentifier.baseIri,
              parts: [...fromNodeMapping.primaryIdentifier.parts],
            },
            relationshipIri: relationshipData.value,
            to: toNodeMapping.key,
          })(model);
        });
      });
    });
  };
